import * as Tone from 'tone';
import * as ndarray from 'ndarray';
import * as resample from 'ndarray-resample';
import { BrushStampsSamplers } from './BrushStampsSamplers';
import { DdspNoWorker } from './DdspNoWorker';
import { unmute } from './unmuteIos.js';
import gsap from 'gsap';

const processAudioCtx = new Tone.Context();

const BASE_MODEL_DURATION = 5; //seconds
const MICRO_DELAY = 0.01; // The delay (in seconds) that we apply to the stamp to make sure it's always played.

// Settings.json meanPitch [62, 59, 69, 63];

const MIN_PITCHES = [57, 52, 57, 68]; // DO = 3e ligne depuis le bas
const MAX_PITCHES = [74, 69, 74, 84];
const TOP_SCREEN_PITCH_MARGIN = 100;
const BOTTOM_SCREEN_PITCH_MARGIN = 100;
const QUANTIZATION = 0.95;

// Settings.json meanLoudness [-69, -57, -73, -71];

const DEFAULT_LOUDNESSES = [-70, -60, -70, -70];
const MIN_LOUDNESS = -120.0;

// Settings.json avgMaxLoudness [-49, -45, -62, -46];

const MAX_LOUDNESSES = [-50, -45, -55, -45];

const KEY_PITCH_CLASSES = [0, 2, 4, 5, 7, 9, 11]; // Gamme majeure // 0 = DO
const KEY_PITCH_KUMOI = [0, 2, 3, 7, 9]; // Gamme Kumoi
const KEY_PITCH_HIRANOSHI = [0, 2, 3, 7, 8]; // Gamme Hiranoshi
const KEY_PITCH_YO = [0, 2, 3, 5, 7, 9, 10]; // Gamme Yo
const KEY_PITCH_CLASSES_STAMPS = [0, 2, 4, 6, 8, 10, 12]; // Gamme augmentée
const NUM_KEY_PITCHES = KEY_PITCH_CLASSES.length;
const NUM_KEY_PITCHES_HIRANOSHI = KEY_PITCH_HIRANOSHI.length;

const FRAMES_PER_SECOND = 250;

const SAMPLE_RATE = 16000;
const MODEL_SAMPLE_RATE = 16000;
const OUTPUT_SAMPLE_RATE = 48000;
const DRUM_VOLUME = -22;

const isSafari = !!window.webkitOfflineAudioContext;
const isWorkerSupported =
  !isSafari && typeof OffscreenCanvas !== 'undefined' ? true : false;

// mp3 files
//const tickSound = require("url:../src/assets/sounds/tick.wav");

// ********************** //
// hack iOS
const ua = navigator.userAgent.toLowerCase();
const isIOS =
  (ua.indexOf('iphone') >= 0 && ua.indexOf('like iphone') < 0) ||
  (ua.indexOf('ipad') >= 0 && ua.indexOf('like ipad') < 0) ||
  (ua.indexOf('ipod') >= 0 && ua.indexOf('like ipod') < 0);

if (isIOS) {
  unmute(processAudioCtx);
}
// ********************** //

const arrayBufferToAudioBuffer = (audioCtx, arrayBuffer, sampleRate) => {
  const newBuffer = audioCtx.createBuffer(1, arrayBuffer.length, sampleRate);
  newBuffer.copyToChannel(arrayBuffer, 0);
  return newBuffer;
};

function getMonoAudio(audioBuffer) {
  if (audioBuffer.numberOfChannels === 1) {
    return audioBuffer.getChannelData(0);
  }
  if (audioBuffer.numberOfChannels !== 2) {
    throw Error(
      `${audioBuffer.numberOfChannels} channel audio is not supported.`
    );
  }
  const ch0 = audioBuffer.getChannelData(0);
  const ch1 = audioBuffer.getChannelData(1);

  const mono = new Float32Array(audioBuffer.length);
  for (let i = 0; i < audioBuffer.length; ++i) {
    mono[i] = (ch0[i] + ch1[i]) / 2;
  }
  return mono;
}

async function resampleAndMakeMono(audioBuffer, targetSr = SAMPLE_RATE) {
  if (audioBuffer.sampleRate === targetSr) {
    return getMonoAudio(audioBuffer);
  }
  const sourceSr = audioBuffer.sampleRate;
  const lengthRes = (audioBuffer.length * targetSr) / sourceSr;
  if (!isSafari) {
    const _offlineCtx = new OfflineAudioContext(
      audioBuffer.numberOfChannels,
      audioBuffer.duration * targetSr,
      targetSr
    );
    const bufferSource = _offlineCtx.createBufferSource();
    bufferSource.buffer = audioBuffer;
    bufferSource.connect(_offlineCtx.destination);
    bufferSource.start();
    return _offlineCtx
      .startRendering()
      .then((buffer) => buffer.getChannelData(0));
  } else {
    // Safari does not support resampling with WebAudio.
    logging.log(
      'Safari does not support WebAudio resampling, so this may be slow.',
      'O&F',
      logging.Level.WARN
    );

    const originalAudio = getMonoAudio(audioBuffer);
    const resampledAudio = new Float32Array(lengthRes);
    resample(
      ndarray(resampledAudio, [lengthRes]),
      ndarray(originalAudio, [originalAudio.length])
    );
    return resampledAudio;
  }
}

class DdspMusicController {
  constructor(loopDuration = 5000, noDDSP = false, shapes, buttons) {
    this.noDDSP = noDDSP;
    this.shapes = shapes;
    this.buttons = buttons;
    this.loopDuration = loopDuration / 1000; // in seconds

    this.ddspWorker =
      !this.noDDSP && isWorkerSupported
        ? new Worker(new URL('./workers/ddspWorker.js', import.meta.url))
        : null;
    this.ddspNoWorker =
      !this.noDDSP && !isWorkerSupported ? new DdspNoWorker() : null;

    this.handlers = {
      onSound: this.onSound.bind(this),
      onDdspWorkerMessage: this.onDdspWorkerMessage.bind(this),
      onSynthesized: this.onSynthesized.bind(this),
    };

    this.loop = new Tone.Part(this.handlers.onSound);
    this.loop.loop = true;
    this.loop.loopEnd = this.loopDuration;
    this.dummyFeatures = [0];
    this.currentInstrumentIndex = 0;
    this.numStrokes = 0;
    this.beat = [];
    this.players = [];
    this.stamps = [];
    this.isBlocked = false;
    this.isTempoActive = false;
    this.sharedAudioLoaderElement = document.getElementById(
      'shared-strokes-loader'
    );
    this.sharedAudioCountElement = document.getElementById(
      'shared-strokes-loader-count'
    );
    this.reverb = new Tone.Reverb({
      decay: 4,
      wet: 0.25,
    }).toDestination();
    //this.tempoSound = new Tone.Player(tickSound).toDestination();
    //this.tempoSound.volume.value = -25;
    this.stampSamplers = new BrushStampsSamplers();
    this.topMargin = TOP_SCREEN_PITCH_MARGIN / window.innerHeight;
    this.bottomMargin = BOTTOM_SCREEN_PITCH_MARGIN / window.innerHeight;
    this.height = 1;
    this.activeAtmosphere = null;
    this.haveSamplesBeenChecked = false;
    this.isGeneratingSharedAudio = false;
    this.sharedAudioIndex = 0;
    this.sharedImageHeight = 0;
    if (this.ddspWorker) {
      this.ddspWorker.addEventListener(
        'message',
        this.handlers.onDdspWorkerMessage
      );
    }
  }

  onDdspWorkerMessage(messageData) {
    if ('event' in messageData.data) {
      if (
        messageData.data.event == 'loadingModelEnd' ||
        messageData.data.event == 'warmupEnded'
      ) {
        this.sendEvent(messageData.data.event);
      } else if (
        messageData.data.event == 'synthesizeEnded' &&
        'trimmedACModified' in messageData.data
      ) {
        this.onSynthesized(messageData.data);
      }
    }
  }

  setAudio(samples, stroke) {
    // make sure the samples are not null (ios)
    if (stroke.points.length > 10 && !this.haveSamplesBeenChecked) {
      this.checkSamples(samples);
    }
    // encode audio
    const data = this.encodeWAV(samples, SAMPLE_RATE);
    const blob = new Blob([data], { type: 'audio/wav' });
    const url = window.URL.createObjectURL(blob);
    // add the new data to the loop
    const delay = stroke.startLoopTime / 1000;
    const soundPlayer = new Tone.Player(url).connect(this.reverb);
    const event = {
      time: delay,
      index: this.players.length,
      player: soundPlayer,
      stamp: false,
    };
    soundPlayer.stroke = stroke;
    soundPlayer.strokeEvent = event;
    this.loop.add(event);
    this.players.push(soundPlayer);

    if (this.isGeneratingSharedAudio && !this.isBlocked) {
      this.sharedAudioIndex++;
      this.generateSharedAudios(this.sharedAudioIndex, this.sharedImageHeight);
    }

    this.sendEvent('audioAdded');
  }

  async onSynthesized(ddspData) {
    const rate = MODEL_SAMPLE_RATE;
    const trimmedACModified = ddspData.trimmedACModified;
    const trimmedAudioBuffer = arrayBufferToAudioBuffer(
      processAudioCtx,
      trimmedACModified,
      rate
    );
    const resampledAudio = await resampleAndMakeMono(trimmedAudioBuffer, rate);
    this.setAudio(resampledAudio, ddspData.stroke);
  }

  checkSamples(samples) {
    let samplesOk = false;
    samples.find((sample) => {
      if (sample > 0) {
        samplesOk = true;
        return true;
      }
      return false;
    });
    this.haveSamplesBeenChecked = true;
    if (!samplesOk) this.sendEvent('setUnsupported');
  }

  init() {
    if (this.ddspWorker) {
      this.ddspWorker.postMessage({ init: true });
    } else if (this.ddspNoWorker) {
      this.ddspNoWorker.load();
    } else {
      this.sendEvent('loadingModelEnd');
    }
  }

  warmUpModel() {
    if (this.ddspWorker) {
      this.ddspWorker.postMessage({ warmup: true });
    } else if (this.ddspNoWorker) {
      this.ddspNoWorker.warmUpModel();
    } else {
      this.sendEvent('warmupEnded');
    }
  }

  initTone() {
    if (Tone.context.state !== 'running') {
      Tone.start();
      Tone.Transport.bpm.value = 120;
      Tone.Transport.start();
    }
  }

  updatePitchMargin(canvasWidth, canvasHeight) {
    this.topMargin = TOP_SCREEN_PITCH_MARGIN / canvasHeight;
    this.bottomMargin = BOTTOM_SCREEN_PITCH_MARGIN / canvasHeight;
    this.height = 1 - this.topMargin - this.bottomMargin;
  }

  onSound(time, data) {
    if (data.player && data.player.loaded) {
      /*const tempoEvent = 'isTempo' in data && data.isTempo === true ? true : false;
      if (tempoEvent && this.isTempoActive && data.player.loaded) {
        data.player.start();
      } else if (!tempoEvent) {
        */
      data.player.start();
      this.shapes.setStrokePlayer(data.player.stroke.id);
      //}
    } else if (data.stamp) {
      this.playStamp(data.y, data.instrument);
    }
  }

  toggleTempo() {
    this.isTempoActive = !this.isTempoActive;
  }

  changeAtmosphere(atmosphereData) {
    this.activeAtmosphere = atmosphereData.id;
    this.reverb.decay = atmosphereData.reverbDecay;
  }

  start() {
    let changed = false;
    if (this.soundScapePlayer) {
      this.soundScapePlayer.start();
    }
    if (this.loop.state !== 'started') {
      changed = true;
      this.loop.start();
    }
    if (Tone.Transport.state !== 'started') {
      changed = true;
      Tone.Transport.start();
    }
    if (changed) {
      this.sendEvent('loopStarted');
    }
  }

  pause() {
    Tone.Transport.pause();
    if (this.soundScapePlayer) {
      this.soundScapePlayer.stop();
    }
    this.sendEvent('loopPaused');
    // pause all sounds !
    for (const player of this.players) {
      if (player.loaded && player.state == 'started') player.stop();
    }
  }

  togglePlayPause() {
    if (Tone.Transport.state === 'started') {
      this.pause();
    } else {
      this.start();
    }
  }

  isPlaying() {
    return Tone.Transport.state === 'started' && this.loop.state === 'started'
      ? true
      : false;
  }

  setCurrentInstrument(index) {
    this.currentInstrumentIndex = index;
  }

  setCurrentStamp(instrument) {
    this.currentStamp = instrument;
  }

  getNumStrokes() {
    return this.numStrokes;
  }

  reset() {
    // tone
    for (const player of this.players) {
      player.dispose();
    }
    this.players = [];
    this.stamps = [];
    this.loop.clear();
    //this.addTempoEvents();
  }

  /*
  addTempoEvents() {
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        this.loop.add({
          time: `${i}:${j}`,
          index: null,
          stamp: false,
          instrument: '',
          player: this.tempoSound,
          isTempo: true,
          y: 0,
        });
      }
    }
  }*/

  addStamp(y, point, currentTime = null, instrument = null) {
    currentTime = currentTime || this.loop.progress * this.loopDuration;
    instrument = instrument || this.currentStamp;
    const event = {
      time: currentTime + MICRO_DELAY,
      index: -1,
      stamp: true,
      instrument: instrument,
      player: null,
      y: y,
      id: point.id,
    };
    this.loop.add(event);
    this.stamps.push(event);
    this.sendEvent('audioAdded');
  }

  block() {
    this.isBlocked = true;
  }

  unblock() {
    this.isBlocked = false;
  }

  adjustY(y, quantization, instrumentIndex) {
    let pitch = this.yToPitch(y, instrumentIndex);
    let adjustedPitch = this.adjustPitchForKey(pitch, quantization);
    return this.pitchToY(adjustedPitch, instrumentIndex);
  }

  yToPitch(y, i) {
    let pitchRange = MAX_PITCHES[i] - MIN_PITCHES[i];
    return MIN_PITCHES[i] + pitchRange * (1 - y);
  }

  pitchToY(pitch, i) {
    let pitchRange = MAX_PITCHES[i] - MIN_PITCHES[i];
    return (1 * (MAX_PITCHES[i] - pitch)) / pitchRange;
  }

  adjustPitchForKey(pitch, quantization) {
    let [floorKeyPitch, ceilKeyPitch] = this.nearestKeyPitches(pitch);
    let alpha = (pitch - floorKeyPitch) / (ceilKeyPitch - floorKeyPitch);
    let centeredAlpha = alpha - 0.5;
    let s = Math.sign(centeredAlpha);
    let a = Math.pow(Math.abs(2 * centeredAlpha), 1.0 - quantization);
    let adjustedAlpha = 0.5 + 0.5 * s * a;
    return (1 - adjustedAlpha) * floorKeyPitch + adjustedAlpha * ceilKeyPitch;
  }

  nearestKeyPitches(pitch) {
    let keyPitchesToUse = KEY_PITCH_CLASSES;
    let numKeyPitches = NUM_KEY_PITCHES;
    // Different scale for japanese atmosphere
    if (this.activeAtmosphere === 'japanese') {
      keyPitchesToUse = KEY_PITCH_HIRANOSHI;
      numKeyPitches = NUM_KEY_PITCHES_HIRANOSHI;
    }
    let pitchClass = pitch % 12;
    for (let i = 0; i < numKeyPitches - 1; i++) {
      if (
        pitchClass >= keyPitchesToUse[i] &&
        pitchClass < keyPitchesToUse[i + 1]
      ) {
        return [
          pitch - pitchClass + keyPitchesToUse[i],
          pitch - pitchClass + keyPitchesToUse[i + 1],
        ];
      }
    }
    if (pitchClass < keyPitchesToUse[0]) {
      return [
        pitch - pitchClass + keyPitchesToUse[numKeyPitches - 1] - 12,
        pitch - pitchClass + keyPitchesToUse[0],
      ];
    } else {
      return [
        pitch - pitchClass + keyPitchesToUse[numKeyPitches - 1],
        pitch - pitchClass + keyPitchesToUse[0] + 12,
      ];
    }
  }

  strokeToAudioFeatures(stroke, screenHeight) {
    const lastPointIndex = stroke.points.length - 1;
    if (lastPointIndex < 1) {
      return null;
    }
    const instrumentIndex = stroke.instrument;
    const startTime = stroke.points[0].time / 1000;
    const totalFrames = Math.floor(BASE_MODEL_DURATION * FRAMES_PER_SECOND);
    const f0_hz = new Array(totalFrames).fill(0.0);
    const loudness_db = new Array(totalFrames).fill(MIN_LOUDNESS);

    let prevFrame = null;
    let prevY = null;
    let i = 0;

    for (const point of stroke.points) {
      const time = point.time / 1000 - startTime;
      const frame = Math.floor((time / BASE_MODEL_DURATION) * totalFrames);
      const y = this.adjustY(point.screenY, QUANTIZATION, instrumentIndex); // TODO change screenY with screenHeight : margin ...

      if (i == 0 || i == lastPointIndex) {
        f0_hz[frame] = this.midiToFrequency(this.yToPitch(y, instrumentIndex));
        loudness_db[frame] = DEFAULT_LOUDNESSES[instrumentIndex]; // TODO: loudness from point.radius
      } else {
        for (let j = prevFrame + 1; j <= frame; j++) {
          // interpolate:
          const frameY =
            prevY + ((y - prevY) * (j - prevFrame)) / (frame - prevFrame);
          f0_hz[j] = this.midiToFrequency(
            this.yToPitch(frameY, instrumentIndex)
          );
          loudness_db[j] = DEFAULT_LOUDNESSES[instrumentIndex]; // TODO: loudness from point.radius
        }
      }
      prevFrame = frame;
      prevY = y;
      i++;
    }

    if (prevFrame < totalFrames) {
      for (i = prevFrame; i < totalFrames; i++) {
        loudness_db[i] = MIN_LOUDNESS;
        f0_hz[i] = -1;
      }
    }

    return { f0_hz, loudness_db };
  }

  getLoopProgress() {
    return this.loop.progress;
  }

  sendEvent(eventName) {
    const event = document.createEvent('Event');
    event.initEvent(eventName, true, true);
    document.dispatchEvent(event);
  }

  remove(shape) {
    let i = 0;
    let event = null;
    if (shape.isStroke) {
      for (const player of this.players) {
        if (player.stroke.id == shape.id) {
          event = player.strokeEvent;
          player.dispose();
          this.players.splice(i, 1);
          break;
        }
        i++;
      }
    } else {
      for (const stampEvent of this.stamps) {
        if (stampEvent.id == shape.id) {
          event = stampEvent;
          this.stamps.splice(i, 1);
          break;
        }
        i++;
      }
    }

    if (event) {
      this.loop.remove(event);
    }
  }

  playStamp(y, instrument) {
    let pitch = this.yToPitch(y, 1);
    if (this.activeAtmosphere === 'japanese') {
      pitch = this.adjustPitchForKey(pitch, QUANTIZATION);
    }
    this.stampSamplers.play(instrument, Math.round(pitch));
  }

  generateSharedAudios(startIndex = 0, imageHeight) {
    this.sharedAudioLoaderElement.style.display = 'block';
    this.sharedImageHeight = imageHeight;
    for (let index = startIndex; index < this.shapes.history.length; index++) {
      this.sharedAudioIndex = index;
      this.sharedAudioCountElement.textContent = `${index + 1}/${
        this.shapes.history.length
      }`;
      const shape = this.shapes.history[index];
      if (shape.isStroke) {
        this.isGeneratingSharedAudio = true;
        // check if the stroke was not drawn over the loop reset
        const numPoints = shape.points.length - 1;
        if (shape.points[0].time > shape.points[numPoints].time) {
          // record the original offset
          const offset = shape.points[0].time;
          let previousOffset = 0;
          shape.points.forEach((point) => {
            // if the current numer is bigger than the offset
            if (point.time >= offset) {
              point.time -= offset;
              previousOffset = point.time;
            }
            // if it started back to 0
            else {
              // add the previous offset to this
              point.time += previousOffset;
            }
          });
        }
        this.generateAudio(shape, imageHeight);
        break;
      } else {
        this.addStamp(
          shape.screenY,
          shape,
          shape.time / 1000,
          this.buttons.getInstrumentId(shape.instrument)
        );
      }
    }

    if (this.sharedAudioIndex >= this.shapes.history.length - 1) {
      this.sendEvent('sharedAudioReady');
      this.sharedAudioCountElement.textContent = `${this.shapes.history.length}/${this.shapes.history.length}`;
      gsap.to(this.sharedAudioLoaderElement, {
        duration: 0.8,
        autoAlpha: 0,
        delay: 0.8,
      });
    }
  }

  setColor(overlayColor) {
    this.sharedAudioLoaderElement.style.color = overlayColor;
  }

  generateAudio(stroke, screenHeight) {
    const features = this.strokeToAudioFeatures(stroke, screenHeight);
    if (features) {
      this.synthesize(stroke, features);
    } else if (this.isGeneratingSharedAudio && !this.isBlocked) {
      this.sharedAudioIndex++;
      this.generateSharedAudios(this.sharedAudioIndex, this.sharedImageHeight);
    }
  }

  synthesize(stroke, audioFeatures) {
    if (this.ddspWorker) {
      this.ddspWorker.postMessage({
        synthesize: true,
        stroke: stroke,
        audioFeatures: audioFeatures,
      });
    } else if (this.ddspNoWorker) {
      this.ddspNoWorker.synthesize(stroke, audioFeatures, (data) => {
        this.setAudio(data.samples, data.stroke);
      });
    } else {
      this.sendEvent('audioAdded');
    }
  }

  midiToFrequency(midi) {
    return 440.0 * Math.pow(2.0, (midi - 69.0) / 12.0);
  }

  yToLoudness(y, i) {
    let loudnessRange = MAX_LOUDNESSES[i] - MIN_LOUDNESS;
    return MIN_LOUDNESS + loudnessRange * (1 - y);
  }

  getKeyPitchLines(instrumentIndex) {
    // Different pitches for the japanese atmosphere
    let keyPitchesToUse = KEY_PITCH_CLASSES;
    if (this.activeAtmosphere === 'japanese') {
      // keyPitchesToUse = KEY_PITCH_YO;
      keyPitchesToUse = KEY_PITCH_HIRANOSHI;
    }
    // different pitches for the stamps
    if (instrumentIndex >= MIN_PITCHES.length) {
      keyPitchesToUse = KEY_PITCH_CLASSES_STAMPS;
      instrumentIndex = 0;
    }
    const pitches = [];
    for (
      let pitch = MIN_PITCHES[instrumentIndex];
      pitch <= MAX_PITCHES[instrumentIndex];
      pitch++
    ) {
      if (keyPitchesToUse.includes(pitch % 12)) {
        const lineY = this.pitchToY(pitch, instrumentIndex);
        pitches.push({
          pitch: pitch,
          y: lineY,
        });
      }
    }
    return pitches;
  }

  encodeWAV(samples) {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);

    const channels = 1;

    /* RIFF identifier */
    this.writeString(view, 0, 'RIFF');
    /* RIFF chunk length */
    view.setUint32(4, 36 + samples.length * 2, true);
    /* RIFF type */
    this.writeString(view, 8, 'WAVE');
    /* format chunk identifier */
    this.writeString(view, 12, 'fmt ');
    /* format chunk length */
    view.setUint32(16, 16, true);
    /* sample format (raw) */
    view.setUint16(20, 1, true);
    /* channel count */
    view.setUint16(22, channels, true);
    /* sample rate */
    view.setUint32(24, SAMPLE_RATE, true);
    /* byte rate (sample rate * block align) */
    view.setUint32(28, SAMPLE_RATE * 4, true);
    /* block align (channel count * bytes per sample) */
    view.setUint16(32, channels * 2, true);
    /* bits per sample */
    view.setUint16(34, 16, true);
    /* data chunk identifier */
    this.writeString(view, 36, 'data');
    /* data chunk length */
    view.setUint32(40, samples.length * 2, true);

    this.floatTo16BitPCM(view, 44, samples);

    return view;
  }

  floatTo16BitPCM(output, offset, input) {
    for (let i = 0; i < input.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, input[i]));
      output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
  }

  writeString(view, offset, str) {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  }
}

export { DdspMusicController };
